import { useState, useEffect } from "react";
import { Clock, MapPin, Loader2, CheckCircle2, LogIn, LogOut, AlertTriangle, Navigation } from "lucide-react";
import api from "@/lib/api";

function formatDuration(hours) {
  if (!hours || hours === 0) return "0 min";
  const totalMin = Math.round(hours * 60);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

const alertLabels = { late: "Atraso", early_leave: "Saída Antecipada", overtime: "Hora Extra", outside_geofence: "Fora da Área" };

export default function ClockPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clocking, setClocking] = useState(false);
  const [location, setLocation] = useState(null);
  const [locError, setLocError] = useState(false);
  const [now, setNow] = useState(new Date());

  const fetchStatus = () => {
    api.get("/workforce/my-status").then((r) => setStatus(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchStatus(); }, []);

  // Live clock in Tokyo time
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setLocError(true),
        { enableHighAccuracy: true }
      );
    }
  }, []);

  const handleClockIn = async () => {
    setClocking(true);
    try {
      await api.post("/workforce/clock-in", { latitude: location?.lat, longitude: location?.lng });
      fetchStatus();
    } catch (e) { alert(e.response?.data?.detail || "Erro"); }
    setClocking(false);
  };

  const handleClockOut = async () => {
    setClocking(true);
    try {
      await api.post("/workforce/clock-out", { latitude: location?.lat, longitude: location?.lng });
      fetchStatus();
    } catch (e) { alert(e.response?.data?.detail || "Erro"); }
    setClocking(false);
  };

  if (loading) return <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-24 crm-card animate-pulse" />)}</div>;

  // Tokyo time display
  const tokyoTime = now.toLocaleTimeString("pt-BR", { timeZone: "Asia/Tokyo", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const tokyoDate = now.toLocaleDateString("pt-BR", { timeZone: "Asia/Tokyo", weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div data-testid="clock-page">
      <h1 className="crm-heading mb-8">Ponto</h1>

      {/* Clock card */}
      <div className="crm-card p-8 max-w-lg mx-auto text-center mb-8">
        <div className="text-5xl font-bold mb-1" style={{ color: "var(--crm-text)", fontFamily: "monospace" }}>{tokyoTime}</div>
        <p className="text-sm mb-1" style={{ color: "var(--crm-text-muted)" }}>{tokyoDate}</p>
        <p className="text-xs mb-6" style={{ color: "var(--crm-text-dim)" }}>Horário de Tokyo (JST)</p>

        {/* Location status */}
        <div className="flex items-center justify-center gap-2 mb-6 text-xs" style={{ color: location ? "#10b981" : locError ? "#ef4444" : "var(--crm-text-dim)" }}>
          <MapPin className="w-4 h-4" />
          {location ? (
            <span>Localização obtida ({location.lat.toFixed(4)}, {location.lng.toFixed(4)})</span>
          ) : locError ? "Localização indisponível" : "Obtendo localização..."}
        </div>

        {/* Clock buttons */}
        {!status?.clocked_in ? (
          <button onClick={handleClockIn} disabled={clocking} data-testid="clock-in-btn"
            className="w-full py-5 rounded-2xl text-lg font-bold transition-all flex items-center justify-center gap-3"
            style={{ background: "#10b981", color: "white", boxShadow: "0 4px 20px rgba(16,185,129,0.3)" }}>
            {clocking ? <Loader2 className="w-6 h-6 animate-spin" /> : <LogIn className="w-6 h-6" />}
            {clocking ? "Registrando..." : "Iniciar Jornada"}
          </button>
        ) : (
          <button onClick={handleClockOut} disabled={clocking} data-testid="clock-out-btn"
            className="w-full py-5 rounded-2xl text-lg font-bold transition-all flex items-center justify-center gap-3"
            style={{ background: "#ef4444", color: "white", boxShadow: "0 4px 20px rgba(239,68,68,0.3)" }}>
            {clocking ? <Loader2 className="w-6 h-6 animate-spin" /> : <LogOut className="w-6 h-6" />}
            {clocking ? "Registrando..." : "Encerrar Jornada"}
          </button>
        )}

        {status?.clocked_in && status?.current_entry && (
          <div className="mt-4 p-3 rounded-xl text-sm flex items-center justify-center gap-2" style={{ background: "rgba(16,185,129,0.08)", color: "#10b981" }}>
            <CheckCircle2 className="w-4 h-4" /> Entrada registrada às {status.current_entry.clock_in}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto mb-8">
        <div className="crm-card p-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--crm-text-muted)" }}>Hoje</p>
          <p className="text-2xl font-bold mt-1" style={{ color: "var(--crm-text)" }}>{formatDuration(status?.total_today)}</p>
        </div>
        <div className="crm-card p-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--crm-text-muted)" }}>Semana</p>
          <p className="text-2xl font-bold mt-1" style={{ color: "var(--crm-text)" }}>{formatDuration(status?.total_week)}</p>
        </div>
      </div>

      {/* Schedule */}
      {(status?.work_config?.work_start || status?.schedule) && (
        <div className="crm-card p-5 max-w-lg mx-auto mb-8">
          <p className="text-sm font-bold mb-2" style={{ color: "var(--crm-text)" }}>Horário de Hoje</p>
          <p className="text-sm" style={{ color: "var(--crm-text-secondary)" }}>
            {status.schedule?.type === "off" ? "Dia de Folga" : status.schedule?.type === "holiday" ? "Feriado" :
              `${status.work_config?.work_start || status.schedule?.start_time || "—"} - ${status.work_config?.work_end || status.schedule?.end_time || "—"}`}
          </p>
          {status.work_config?.work_days?.length > 0 && (
            <div className="flex gap-1.5 mt-2">
              {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d, i) => (
                <span key={i} className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                  style={{
                    background: status.work_config.work_days.includes(i) ? "var(--crm-accent-subtle)" : "var(--crm-input-bg)",
                    color: status.work_config.work_days.includes(i) ? "var(--crm-accent)" : "var(--crm-text-dim)",
                  }}>{d}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Today entries */}
      {status?.today_entries?.length > 0 && (
        <div className="crm-card overflow-hidden max-w-lg mx-auto">
          <div className="px-5 py-3" style={{ borderBottom: "1px solid var(--crm-border)" }}>
            <p className="text-sm font-bold" style={{ color: "var(--crm-text)" }}>Registros de Hoje</p>
          </div>
          {status.today_entries.map((e) => (
            <div key={e.id} className="px-5 py-4" style={{ borderBottom: "1px solid var(--crm-border)" }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4" style={{ color: "var(--crm-text-dim)" }} />
                  <span className="text-sm font-medium" style={{ color: "var(--crm-text)" }}>
                    {e.clock_in} — {e.clock_out || "..."}
                  </span>
                  {e.total_hours != null && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ background: "var(--crm-accent-subtle)", color: "var(--crm-accent)" }}>
                      {formatDuration(e.total_hours)}
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  {e.alerts?.map((a) => (
                    <span key={a} className="text-[10px] font-bold px-2 py-0.5 rounded-md" style={{
                      background: a === "late" || a === "outside_geofence" ? "rgba(239,68,68,0.1)" : a === "overtime" ? "rgba(59,130,246,0.1)" : "rgba(249,115,22,0.1)",
                      color: a === "late" || a === "outside_geofence" ? "#ef4444" : a === "overtime" ? "#3b82f6" : "#f97316",
                    }}>{alertLabels[a] || a}</span>
                  ))}
                </div>
              </div>
              {/* Location info */}
              <div className="flex flex-wrap gap-3 text-[10px]" style={{ color: "var(--crm-text-dim)" }}>
                {e.clock_in_lat != null && (
                  <span className="flex items-center gap-1">
                    <Navigation className="w-3 h-3" /> Entrada: {e.clock_in_lat.toFixed(4)}, {e.clock_in_lng.toFixed(4)}
                    {e.clock_in_geofence === "valid" && <span style={{ color: "#10b981" }}>(OK)</span>}
                    {e.clock_in_geofence === "outside" && <span style={{ color: "#ef4444" }}>(Fora)</span>}
                  </span>
                )}
                {e.clock_out_lat != null && (
                  <span className="flex items-center gap-1">
                    <Navigation className="w-3 h-3" /> Saída: {e.clock_out_lat.toFixed(4)}, {e.clock_out_lng.toFixed(4)}
                    {e.clock_out_geofence === "valid" && <span style={{ color: "#10b981" }}>(OK)</span>}
                    {e.clock_out_geofence === "outside" && <span style={{ color: "#ef4444" }}>(Fora)</span>}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
